// src/components/SupplierLayout.js
import React, { useState, useEffect, useContext } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import { fetchRegions } from '../services/api';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import './Layout.css'; // reuse existing styles
import ChatWidget from './ChatWidget';

function SupplierLayout({ children }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [availableRegions, setAvailableRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const { user } = useContext(AuthContext);
  const { selectedRegion, updateRegion, allowedRegions, canAccessRegion } = useRegion();

  const navigate = useNavigate();

  useEffect(() => {
    loadRegions();
    checkProfileCompletion();
  }, [user]);

  const loadRegions = async () => {
    try {
      const regions = await fetchRegions();
      setAvailableRegions(regions);
    } catch (error) {
      console.error('❌ Error loading regions:', error);
      setAvailableRegions([
        { id: 'vquest', name: 'New Zealand' },
        { id: 'vquest-au-1', name: 'Australia' },
        { id: 'vquest-irl-1', name: 'Ireland' },
        { id: 'vquest-us-1', name: 'USA/Canada' }
      ]);
    } finally {
      setRegionsLoading(false);
    }
  };

  const checkProfileCompletion = async () => {
    if (user?.uid) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserProfile(userData);
          
          // Check if profile is completed
          const isCompleted = userData.profileCompleted || (
            userData.companyProfile?.companyName &&
            userData.companyProfile?.primaryContact &&
            userData.companyProfile?.phoneNumber &&
            userData.companyProfile?.email &&
            userData.companyProfile?.website
          );
          
          setProfileCompleted(isCompleted);
        }
      } catch (error) {
        console.error('Error checking profile completion:', error);
      }
    }
  };

  const handleRegionChange = (e) => {
    const newRegionId = e.target.value;

    if (allowedRegions.length > 0 && !canAccessRegion(newRegionId)) {
      alert(`Access denied to region: ${newRegionId}. Contact your administrator for access.`);
      return;
    }

    updateRegion(newRegionId);
    setTimeout(() => window.location.reload(), 100);
  };

  const getUserAccessibleRegions = () => {
    if (allowedRegions.length === 0) return availableRegions;
    return availableRegions.filter((r) => allowedRegions.includes(r.id));
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') handleSearch();
  };

  const handleProtectedNavigation = (path) => {
    if (!profileCompleted) {
      alert('Please complete your company profile first to access this feature.');
      navigate('/supplier/dashboard');
      return;
    }
    navigate(path);
  };

  return (
    <div className="layout">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="logo">
          <Link to="/supplier/dashboard">
            <img src="/icons/Nezo_Logo_White.png" alt="Logo" />
          </Link>
        </div>
        <hr />

        {/* Dashboard */}
        <NavLink
          to="/supplier/dashboard"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
          end
        >
          <img src="/icons/computer.png" alt="Dashboard Icon" />
          Dashboard
        </NavLink>

        <hr />
        {/* Material Library */}
        <NavLink
            to="/categories"
            className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
        >
            <img src="/icons/library.png" alt="Material Library Icon" />
            Material Library
        </NavLink>

        {/* Submit New Material */}
        <div 
          className={`menu-item ${!profileCompleted ? 'menu-item-disabled' : ''}`}
          onClick={() => profileCompleted ? navigate('/material-requests') : handleProtectedNavigation('/material-requests')}
          style={{ cursor: profileCompleted ? 'pointer' : 'not-allowed' }}
        >
          <img src="/icons/components.png" alt="Submit Material Icon" />
          New Product
          {!profileCompleted && <span className="lock-icon">🔒</span>}
        </div>

        {/* My Submissions */}
        <div 
          className={`menu-item ${!profileCompleted ? 'menu-item-disabled' : ''}`}
          onClick={() => profileCompleted ? navigate('/supplier/my-submissions') : handleProtectedNavigation('/supplier/my-submissions')}
          style={{ cursor: profileCompleted ? 'pointer' : 'not-allowed' }}
        >
          <img src="/icons/list.png" alt="My Submissions Icon" />
          My Submissions
          {!profileCompleted && <span className="lock-icon">🔒</span>}
        </div>

        {/* Documentation */}
        <div 
          className={`menu-item ${!profileCompleted ? 'menu-item-disabled' : ''}`}
          onClick={() => profileCompleted ? navigate('/supplier/documentation') : handleProtectedNavigation('/supplier/documentation')}
          style={{ cursor: profileCompleted ? 'pointer' : 'not-allowed' }}
        >
          <img src="/icons/computer.png" alt="Documentation Icon" />
          Documentation
          {!profileCompleted && <span className="lock-icon">🔒</span>}
        </div>

        <hr />

        {/* Account */}
        <NavLink
          to="/account"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
        >
          <img src="/icons/person.png" alt="Account Icon" />
          Account
        </NavLink>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Bar */}
        <div className="top-bar">
          <div className="search-container">
            <input
              className="search-bar"
              type="text"
              placeholder="Search materials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            <button className="search-btn" onClick={handleSearch}>
              Search
            </button>
          </div>

          <div className="top-buttons">
            {/* Company Name Display */}
            {userProfile?.companyProfile?.companyName && (
              <div className="company-name-display">
                🏢 {userProfile.companyProfile.companyName}
              </div>
            )}

            {/* Region Dropdown */}
            <div className="region-dropdown-container">
              {regionsLoading ? (
                <div className="region-loading">🌏</div>
              ) : (
                <select
                  value={selectedRegion || ''}
                  onChange={handleRegionChange}
                  className="region-dropdown"
                  title="Switch Region"
                >
                  <option value="" disabled>
                    Select Region
                  </option>
                  {getUserAccessibleRegions().map((region) => (
                    <option key={region.id} value={region.id}>
                      🌏 {region.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Profile Completion Warning */}
            {!profileCompleted && (
              <div className="profile-warning" title="Complete your profile to access all features">
                ⚠️
              </div>
            )}

            <button className="top-btn" title="Notifications">🔔</button>
            
            <button className="top-btn" title="Messages">
              <Link to="/messages">✉️</Link>
            </button>         
            <button className="top-btn" title="Account">
              <Link to="/account">👤</Link>
            </button>
          </div>
        </div>

        {/* Profile Completion Banner */}
        {!profileCompleted && (
          <div className="profile-incomplete-banner" style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            padding: '10px 20px',
            margin: '0 20px 20px',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>⚠️ Complete your company profile to access all supplier features</span>
            <button 
              onClick={() => navigate('/supplier/dashboard')}
              style={{
                background: '#f39c12',
                color: 'white',
                border: 'none',
                padding: '5px 15px',
                borderRadius: '3px',
                cursor: 'pointer'
              }}
            >
              Complete Profile
            </button>
          </div>
        )}

        {/* Page Content */}
        <div className="content-container">{children}</div>
      </div>
      <ChatWidget />
    </div>
  );
}

export default SupplierLayout;